import { Box, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';

import { FlowChartProvider } from '@/components/diagrams/FlowChart';
import { PreviewWithBrandingToggle } from '@/components/diagrams/FlowChart/components/Controls/PreviewWithBrandingToggle';
import { useFlowChartContext } from '@/components/diagrams/FlowChart/context/flowChart.context';
import { useIsInteractive } from '@/components/diagrams/FlowChart/hooks/useIsInteractive';
import { Button } from '@/components/form/baseInputs/Button';
import { FullScreenModal } from '@/components/modals/FullScreenModal/FullScreenModal';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { LoadingOverlay } from '@/components/progress/LoadingOverlay/LoadingOverlay';
import { useOnBeforeUnloadPrompt } from '@/hooks/useOnBeforeUnloadPrompt';
import { EntityDiagram } from '@/modules/entityDiagram';
import { useEntityDiagramContext } from '@/modules/entityDiagram/contexts/entityDiagram.context';
import { EntityDiagramProvider } from '@/modules/entityDiagram/contexts/EntityDiagram.provider';
import { mapEntityDiagramVariantToKind } from '@/modules/entityDiagram/EntityDiagram.utils';
import { useQueryEntityDiagram } from '@/modules/entityDiagram/hooks';
import { EntityDiagramVariant } from '@/modules/entityDiagram/types';
import {
  useBatchCreateEntityDiagramMutation,
  useBatchUpdateGraphVisualizationMutation,
} from '@/modules/graphViz/graphql/BatchGraphVisualization.generated';
import { diffVisualizationGroups } from '@/modules/graphViz/graphVizGroup/grouping';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { TrowserKey } from '@/modules/navigation/trowsers/trowser.types';
import { COLORS } from '@/styles/tokens/colors';
import { GraphVisualizationOwnerType } from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';

import {
  buildCreateEntityDiagramInput,
  buildNodeConfigurations,
} from './EditEntityDiagramTrowser.utils';

const TROWSER_HEADER_HEIGHT = 68;

interface EditEntityDiagramTrowserInnerProps {
  householdId: string;
  entityId: string;
  onClose: (didSave?: boolean) => void;
}

function EditEntityDiagramTrowserInner({
  householdId,
  entityId,
  onClose,
}: EditEntityDiagramTrowserInnerProps) {
  const { dispatch, getState, state } = useEntityDiagramContext();
  const { isDirty } = useFlowChartContext();
  const { toggleIsInteractive, getIsInteractive } = useIsInteractive();
  const { showFeedback } = useFeedback();
  const [isSaving, setIsSaving] = useState(false);

  const [createEntityDiagram] = useBatchCreateEntityDiagramMutation();
  const [updateEntityDiagram] = useBatchUpdateGraphVisualizationMutation();

  useEffect(() => {
    if (!getIsInteractive()) {
      toggleIsInteractive();
    }
  }, [getIsInteractive, toggleIsInteractive]);

  useOnBeforeUnloadPrompt(isDirty);

  const handleSave = useCallback(async () => {
    if (!isDirty) {
      onClose(false);
      return;
    }

    setIsSaving(true);

    // Use getState() instead of reading from context's `state` to avoid stale closure values
    const state = getState();
    const {
      entityDiagramId,
      entity,
      entityDiagramVariant,
      selectedPrimaryClientId,
      nodes,
      visualizationGroups,
      originalVisualizationGroups,
      hideEntitiesListInGroups,
    } = state;

    const nodeConfigurations = buildNodeConfigurations(nodes);
    const groupDiff = diffVisualizationGroups(
      originalVisualizationGroups,
      visualizationGroups
    );

    try {
      if (entityDiagramId) {
        await updateEntityDiagram({
          variables: {
            input: {
              ownerID: entityDiagramId,
              ownerType: GraphVisualizationOwnerType.EntityDiagram,
              nodeConfigurations,
              hideEntitiesListInGroups,
              ...(entityDiagramVariant ===
                EntityDiagramVariant.Dispositions && {
                firstGrantorDeathID: selectedPrimaryClientId,
              }),
              ...groupDiff,
            },
          },
        });
      } else {
        const kind = mapEntityDiagramVariantToKind(entityDiagramVariant);
        const create = buildCreateEntityDiagramInput({
          householdId: entity.household.id,
          entityId: entity.id,
          kind,
          nodeConfigurations,
          hideEntitiesListInGroups,
          entityDiagramVariant,
          selectedPrimaryClientId,
        });
        await createEntityDiagram({
          variables: {
            input: { create, createGroups: groupDiff.createGroups },
          },
        });
      }

      showFeedback('Changes saved successfully.', { variant: 'success' });

      setIsSaving(false);
      onClose(true);
    } catch (err) {
      showFeedback('Something went wrong. Please try again.');
      diagnostics.error('Failed to save entity diagram', err as Error, {
        entityId,
        entityDiagramId,
      });
      setIsSaving(false);
    }
  }, [
    isDirty,
    getState,
    updateEntityDiagram,
    createEntityDiagram,
    showFeedback,
    entityId,
    onClose,
  ]);

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <Box height="100%" display="flex" flexDirection="column">
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        px={2}
        height={TROWSER_HEADER_HEIGHT}
        minHeight={TROWSER_HEADER_HEIGHT}
        borderBottom={1}
        borderColor={COLORS.GRAY[200]}
      >
        <Typography variant="title-l">Edit diagram</Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <PreviewWithBrandingToggle
            checked={state.renderWhiteLabeled}
            onChange={(checked) =>
              dispatch({
                type: 'SET_RENDER_WHITE_LABELED',
                renderWhiteLabeled: checked,
              })
            }
          />
          <Stack direction="row" spacing={1}>
            <Button variant="secondary" size="sm" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleSave}
              loading={isSaving}
              data-testid="edit-entity-diagram-save-button"
            >
              Done
            </Button>
          </Stack>
        </Stack>
      </Stack>
      <Box flex={1} minHeight={0}>
        <AutoSizer>
          {({ height, width }: { height: number; width: number }) => (
            <EntityDiagram
              householdId={householdId}
              entityId={entityId}
              height={height}
              width={width}
              hideFullscreenButton
              hideBorder
            />
          )}
        </AutoSizer>
      </Box>
    </Box>
  );
}

export interface EditEntityDiagramTrowserProps {
  onClose: (didSave?: boolean) => void;
  householdId: string;
  entityId: string;
  entityDiagramVariant: EntityDiagramVariant;
  selectedPrimaryClientId: string;
}

export function EditEntityDiagramTrowser({
  onClose,
  householdId,
  entityId,
  entityDiagramVariant,
  selectedPrimaryClientId,
}: EditEntityDiagramTrowserProps) {
  const { primaryClients, isTwoClientHousehold } = useHouseholdDetailsContext();

  const {
    entity,
    entityDiagram,
    recipientDispositions,
    bdgDefaultTriggerClientId,
    isBdgEntity,
    isAwaitingPouroverLookThrough,
  } = useQueryEntityDiagram({
    entityId,
    entityDiagramVariant,
    selectedPrimaryClientId,
  });

  // Gate only on the root entity so the modal opens promptly; the look-through
  // is gated separately below.
  const hasData =
    (primaryClients?.length ?? 0) > 0 && !!entity && !!selectedPrimaryClientId;

  if (!hasData) {
    return null;
  }

  return (
    <FullScreenModal
      trowserKey={TrowserKey.EditEntityDiagram}
      isOpen
      onClose={() => onClose()}
      backdropSx={{ overflow: 'hidden' }}
    >
      {isAwaitingPouroverLookThrough ? (
        // Wait for pour-over depth-3 data before mounting the editable diagram
        // so FlowChartProvider mounts once with complete recipientDispositions;
        // otherwise an in-flight look-through would change the key and remount
        // the provider mid-edit, discarding the user's unsaved layout changes.
        <LoadingOverlay open updatingText="Loading diagram..." />
      ) : (
        <FlowChartProvider
          key={`${entityId}-${JSON.stringify(recipientDispositions)}`}
        >
          <EntityDiagramProvider
            primaryClients={primaryClients ?? []}
            isTwoClientHousehold={isTwoClientHousehold}
            entity={entity}
            entityDiagram={entityDiagram}
            presentationMode={false}
            viewOnlyMode={false}
            selectedPrimaryClientId={selectedPrimaryClientId}
            entityDiagramVariant={entityDiagramVariant}
            recipientDispositions={recipientDispositions}
            bdgDefaultTriggerClientId={bdgDefaultTriggerClientId}
            isBdgEntity={isBdgEntity}
            initialRenderWhiteLabeled
          >
            <EditEntityDiagramTrowserInner
              householdId={householdId}
              entityId={entityId}
              onClose={onClose}
            />
          </EntityDiagramProvider>
        </FlowChartProvider>
      )}
    </FullScreenModal>
  );
}
